import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { User } from '~/eds/src/components/User/User';
import { Form } from '~/eds/src/modules/Form/Form';
import { StateUpdater } from '~/eds/src/modules/Form/types';
import {
  requiredValidator,
  wordLengthValidator,
} from '~/eds/src/modules/Form/validators';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { Question } from '~/types/QuestionGroup';

import { QuestionGroupSelect } from './QuestionGroupSelect';

interface EditQuestionFormProps {
  question: Question;
}

type QuestionForm = {
  name: string;
  text: string;
  questionGroups: string[];
};

export const useEditQuestionModal = ({ question }: EditQuestionFormProps) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const [questionData, setQuestionData] = useState<Question>(question);
  const [hasErrors, setHasErrors] = useState(false);
  const { users } = useResolveUsers({
    userIds: [question.createdBy],
  });

  const { toast } = useToast();

  const [updateQuestion, { isLoading: isUpdating }] =
    api.endpoints.updateQuestion.useMutation();

  const fields = {
    name: {
      key: 'name',
      type: 'field' as const,
      name: 'name',
      label: intl.formatMessage({
        id: 'UseEditQuestionModal.nameLabel',
        defaultMessage: 'Saved Question Name',
        description: 'Label for the question name field',
      }),
      optional: true,
      input: TextInput,
      placeholder: intl.formatMessage({
        id: 'UseEditQuestionModal.namePlaceholder',
        defaultMessage: 'e.g. Contract Law Compliance',
        description: 'Placeholder text for question name field',
      }),
    },
    text: {
      key: 'text',
      type: 'field' as const,
      name: 'text',
      label: intl.formatMessage({
        id: 'UseEditQuestionModal.textLabel',
        defaultMessage: 'Saved Question',
        description: 'Label for the question text field',
      }),
      input: TextArea,
    },
    questionGroups: {
      key: 'questionGroups',
      type: 'field' as const,
      name: 'questionGroups',
      label: intl.formatMessage({
        id: 'UseEditQuestionModal.questionGroupsLabel',
        defaultMessage: 'Add to Question Group(s)',
        description: 'Label for question groups selection field',
      }),
      description: intl.formatMessage({
        id: 'UseEditQuestionModal.questionGroupsDescription',
        defaultMessage: 'Search and select the question group(s).',
        description: 'Description for question groups selection field',
      }),
      input: QuestionGroupSelect,
    },
  };

  const questionInitialState = {
    name: { value: question.name },
    text: { value: question.text },
    questionGroups: { value: question.questionGroupIds },
  };

  const handleUpdateQuestion = async () => {
    try {
      await updateQuestion({
        id: question.id,
        name: questionData.name,
        text: questionData.text,
        questionGroupIds: questionData.questionGroupIds,
      }).unwrap();
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'UseEditQuestionModal.updateSuccessMessage',
          defaultMessage: 'The question has been successfully updated',
          description: 'Success message when question is updated',
        }),
      });
    } catch (apiError: any) {
      toast({
        status: 'danger',
        message: intl.formatMessage(
          {
            id: 'UseEditQuestionModal.updateErrorMessage',
            defaultMessage: 'The question updated failed due to {errorTitle}.',
            description:
              'Error message when question update fails. Example: errorTitle = "Network Error"',
          },
          {
            errorTitle: apiError.errors[0].title,
          },
        ),
      });
    }
  };

  const handleOnChange = ({ state, errors }: StateUpdater<QuestionForm>) => {
    setHasErrors(Object.values(errors || {}).some((error) => error.length > 0));
    setQuestionData({
      ...question,
      name: state.name.value,
      text: state.text.value,
      questionGroupIds: state.questionGroups.value,
    });
  };

  const renderForm = () => {
    return (
      <>
        <Form<QuestionForm>
          fields={fields}
          onChange={handleOnChange}
          state={questionInitialState}
          validators={[
            requiredValidator(['text'], intl),
            wordLengthValidator(['text'], 1000, intl),
          ]}
        />
        <Layout flex={1} my={4} spacing={4}>
          <Layout direction="column" flex={1}>
            <Text variant="tiny">
              {intl.formatMessage({
                id: 'UseEditQuestionModal.createdLabel',
                defaultMessage: 'Created',
                description: 'Label for creation information section',
              })}
            </Text>
            <Layout spacing={1} direction="column">
              <Layout spacing={2}>
                <Icon icon="field-date" size="s" />
                <Text>{formatDate(question.createdAt, 'long')}</Text>
              </Layout>
              <Layout spacing={2}>
                <Icon icon="user" size="s" />
                <User user={users?.[question.createdBy]} mode="name" />
              </Layout>
            </Layout>
          </Layout>
          <Layout direction="column" flex={1}>
            <Text variant="tiny">
              {intl.formatMessage({
                id: 'UseEditQuestionModal.modifiedLabel',
                defaultMessage: 'Modified',
                description: 'Label for modification information section',
              })}
            </Text>
            <Layout spacing={1} direction="column">
              <Layout spacing={2}>
                <Icon icon="field-date" size="s" />
                <Text>{formatDate(question.updatedAt, 'long')}</Text>
              </Layout>
              <Layout spacing={2}>
                <Icon icon="user" size="s" />
                <User user={users?.[question.createdBy]} mode="name" />
              </Layout>
            </Layout>
          </Layout>
        </Layout>
      </>
    );
  };

  const modal = useModal({
    title: intl.formatMessage({
      id: 'UseEditQuestionModal.title',
      defaultMessage: 'Edit',
      description: 'Title for the edit question modal',
    }),
    children: renderForm(),
    primaryAction: {
      text: intl.formatMessage({
        id: 'UseEditQuestionModal.saveAction',
        defaultMessage: 'Save',
        description: 'Save button text for edit question modal',
      }),
      onClick: handleUpdateQuestion,
      isLoading: isUpdating,
      disabled: hasErrors,
      tooltip: hasErrors
        ? intl.formatMessage({
            id: 'UseEditQuestionModal.fillRequiredFieldsTooltip',
            defaultMessage: 'Please fill in all required fields',
            description: 'Tooltip when required fields are not filled',
          })
        : undefined,
    },
  });

  return modal;
};
